// CategoryCard.tsx — Digital Sanctuary Design System
// Category selection card with Ionicons (replaces emoji icons) and DS tokens

import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { View, Dimensions, StyleSheet } from "react-native";

import { getCategoryIcon } from "../constants/categoryIcons";
import { DSText } from "../design-system/components/DSText";
import { useAppTheme } from "../design-system/ThemeProvider";
import { spacing, radius } from "../design-system/tokens";
import { iqesColors } from "../shared/ui/iqesDesignSystem";

import GlowTouchable from "./GlowTouchable";

interface CategoryCardProps {
  name: string;
  icon?: string;
  description?: string;
  onPress: () => void;
  color?: string;
  style?: StyleProp<ViewStyle>;
  categoryId?: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  name,
  icon: _icon,
  description,
  onPress,
  style,
  categoryId,
}) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const { icon: ioniconName, gradientColors } = getCategoryIcon(categoryId || 0);
  const [startColor] = gradientColors;
  const cardWidth = Dimensions.get('window').width * 0.35;

  return (
    <GlowTouchable
      onPress={onPress}
      style={[
        styles.card,
        { width: cardWidth },
        style,
      ]}
      accessibilityLabel={name}
      accessibilityRole="button"
    >
      <LinearGradient
        colors={['rgba(246,190,58,0.2)', 'rgba(76,29,120,0.22)', 'rgba(3,5,16,0.86)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={[styles.softOrb, { backgroundColor: startColor }]} />
      <View style={styles.goldEdge} />

      {/* Icon */}
      <View style={styles.iconContainer}>
        <Ionicons name={ioniconName} size={32} color={iqesColors.goldSoft} />
      </View>

      {/* Title */}
      <DSText
        variant="label"
        color={iqesColors.ivory}
        align="center"
        numberOfLines={2}
        adjustsFontSizeToFit
        style={styles.title}
      >
        {name}
      </DSText>

      {/* Description */}
      {description ? (
        <DSText
          variant="caption"
          color="rgba(255,248,232,0.72)"
          align="center"
          numberOfLines={3}
          style={styles.description}
        >
          {description}
        </DSText>
      ) : null}
    </GlowTouchable>
  );
};

type ThemeColors = ReturnType<typeof useAppTheme>["colors"];

const createStyles = (_colors: ThemeColors) => StyleSheet.create({
  card: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: radius.lg + 2,
    margin: spacing.sm - 2,
    padding: spacing.md,
    paddingHorizontal: spacing.sm + 2,
    backgroundColor: iqesColors.canvasRaised,
    borderWidth: 1,
    borderColor: 'rgba(246,190,58,0.36)',
    aspectRatio: 0.85,
    shadowColor: iqesColors.gold,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 18,
    elevation: 8,
  },
  goldEdge: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: iqesColors.gold,
    borderTopLeftRadius: radius.lg + 2,
    borderTopRightRadius: radius.lg + 2,
  },
  softOrb: {
    position: 'absolute',
    width: 96,
    height: 96,
    borderRadius: 48,
    top: -28,
    right: -28,
    opacity: 0.16,
  },
  iconContainer: {
    marginBottom: spacing.sm,
    padding: spacing.sm + 4,
    borderRadius: radius.xl,
    backgroundColor: 'rgba(246,190,58,0.13)',
    borderWidth: 1,
    borderColor: 'rgba(246,190,58,0.46)',
  },
  title: {
    marginBottom: spacing.xs,
    fontWeight: '900',
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 0.2,
  },
  description: {
    lineHeight: 17,
  },
});

export default CategoryCard;
